import React, { useState } from 'react';
import {
  User as UserIcon, LogOut, Settings, Sparkles, Menu, X,
  Home, LayoutDashboard, CreditCard, HelpCircle, Shield, FileText,
  Globe, Check, ChevronDown
} from 'lucide-react';
import type { User, UserQuotaInfo } from '@vidly/types';
import type { LegalTabType } from './LegalView';
import { useLanguage } from '../context/LanguageContext';

interface NavbarProps {
  user: User | null;
  quota: UserQuotaInfo | null;
  currentView: 'home' | 'dashboard' | 'workflow' | 'pricing' | 'legal' | 'guides';
  legalTab?: LegalTabType;
  onNavigate: (view: 'home' | 'dashboard' | 'pricing' | 'legal' | 'guides', tab?: LegalTabType) => void;
  onLoginClick: () => void;
  onLogoutClick: () => void;
  onUpgradeClick: () => void;
  onSettingsClick?: () => void;
}

export const Navbar: React.FC<NavbarProps> = ({
  user,
  quota,
  currentView,
  legalTab,
  onNavigate,
  onLoginClick,
  onLogoutClick,
  onUpgradeClick,
  onSettingsClick
}) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const { language, setLanguage, t } = useLanguage();

  const handleNav = (view: 'home' | 'dashboard' | 'pricing' | 'legal' | 'guides', tab?: LegalTabType) => {
    setMobileMenuOpen(false);
    onNavigate(view, tab);
  };

  return (
    <header className="sticky top-0 z-40 w-full bg-[#09090b]/95 backdrop-blur-md border-b border-zinc-800/80 text-zinc-200">
      <div className="max-w-6xl mx-auto px-3 sm:px-6 h-14 flex items-center justify-between gap-2 sm:gap-4">
        
        {/* Brand / Logo */}
        <button
          onClick={() => handleNav('home')}
          className="flex items-center gap-2.5 focus:outline-none group text-left shrink-0"
        >
          <img
            src="/vidly-logo.png"
            alt="Vidly AI"
            className="w-7 h-7 object-contain invert brightness-125 group-hover:scale-105 transition-transform"
          />
          <span className="font-semibold text-sm tracking-tight text-white group-hover:text-zinc-200 transition-colors">
            Vidly AI
          </span>
          <span className="text-[10px] text-zinc-300 font-medium px-2 py-0.5 rounded-full border border-zinc-800 bg-zinc-900/60 hidden xs:inline-flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
            beta 1.0
          </span>
        </button>

        {/* Desktop Navigation Tabs (Hidden on mobile) */}
        <nav className="hidden md:flex items-center gap-1 text-xs font-medium">
          <button
            onClick={() => handleNav('home')}
            className={`px-3 py-1.5 rounded-lg transition-colors whitespace-nowrap ${
              currentView === 'home'
                ? 'bg-zinc-800 text-white font-medium'
                : 'text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900/60'
            }`}
          >
            {t.nav.home}
          </button>

          {user && (
            <button
              onClick={() => handleNav('dashboard')}
              className={`px-3 py-1.5 rounded-lg transition-colors whitespace-nowrap ${
                currentView === 'dashboard' || currentView === 'workflow'
                  ? 'bg-zinc-800 text-white font-medium'
                  : 'text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900/60'
              }`}
            >
              {t.nav.dashboard}
            </button>
          )}

          <button
            onClick={() => handleNav('pricing')}
            className={`px-3 py-1.5 rounded-lg transition-colors whitespace-nowrap ${
              currentView === 'pricing'
                ? 'bg-zinc-800 text-white font-medium'
                : 'text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900/60'
            }`}
          >
            {t.nav.pricing}
          </button>

          <button
            onClick={() => handleNav('guides')}
            className={`px-3 py-1.5 rounded-lg transition-colors whitespace-nowrap ${
              currentView === 'guides'
                ? 'bg-zinc-800 text-white font-medium'
                : 'text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900/60'
            }`}
          >
            {t.nav.guides}
          </button>

          <button
            onClick={() => handleNav('legal', 'faq')}
            className={`px-3 py-1.5 rounded-lg transition-colors whitespace-nowrap ${
              currentView === 'legal' && legalTab === 'faq'
                ? 'bg-zinc-800 text-white font-medium'
                : 'text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900/60'
            }`}
          >
            {t.nav.howItWorks}
          </button>

          <button
            onClick={() => handleNav('legal', 'privacy')}
            className={`px-3 py-1.5 rounded-lg transition-colors whitespace-nowrap ${
              currentView === 'legal' && (legalTab === 'privacy' || legalTab === 'cookies')
                ? 'bg-zinc-800 text-white font-medium'
                : 'text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900/60'
            }`}
          >
            {t.nav.privacy}
          </button>

          <button
            onClick={() => handleNav('legal', 'terms')}
            className={`px-3 py-1.5 rounded-lg transition-colors whitespace-nowrap ${
              currentView === 'legal' && legalTab === 'terms'
                ? 'bg-zinc-800 text-white font-medium'
                : 'text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900/60'
            }`}
          >
            {t.nav.terms}
          </button>
        </nav>

        {/* Right side: Language Selector, User Quota, Actions & Mobile Hamburger */}
        <div className="flex items-center gap-1.5 sm:gap-2 text-xs shrink-0">
          
          {/* Language Selector Dropdown */}
          <div className="relative">
            <button
              onClick={() => setLangMenuOpen(!langMenuOpen)}
              className="flex items-center gap-1.5 px-2 sm:px-2.5 py-1.5 rounded-lg border border-zinc-800 bg-zinc-900/60 hover:bg-zinc-800 text-zinc-300 hover:text-white transition-colors text-xs font-medium"
              title={language === 'en' ? 'Switch to Spanish' : 'Switch to English'}
              aria-label="Language selector"
            >
              <Globe className="w-3.5 h-3.5 text-zinc-400" />
              <span className="uppercase text-[11px] font-semibold tracking-wider">
                {language}
              </span>
              <ChevronDown className={`w-3 h-3 text-zinc-500 transition-transform ${langMenuOpen ? 'rotate-180' : ''}`} />
            </button>

            {langMenuOpen && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setLangMenuOpen(false)}
                />
                <div className="absolute right-0 mt-1.5 w-36 py-1 rounded-xl bg-zinc-950 border border-zinc-800 shadow-2xl z-50 animate-in fade-in zoom-in-95 duration-150">
                  <button
                    onClick={() => {
                      setLanguage('en');
                      setLangMenuOpen(false);
                    }}
                    className={`w-full px-3 py-2 text-left text-xs flex items-center justify-between transition-colors ${
                      language === 'en'
                        ? 'text-emerald-400 font-semibold bg-zinc-900/80'
                        : 'text-zinc-400 hover:text-white hover:bg-zinc-900/40'
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <span className="text-sm">🇺🇸</span>
                      <span>English</span>
                    </span>
                    {language === 'en' && <Check className="w-3.5 h-3.5" />}
                  </button>

                  <button
                    onClick={() => {
                      setLanguage('es');
                      setLangMenuOpen(false);
                    }}
                    className={`w-full px-3 py-2 text-left text-xs flex items-center justify-between transition-colors ${
                      language === 'es'
                        ? 'text-emerald-400 font-semibold bg-zinc-900/80'
                        : 'text-zinc-400 hover:text-white hover:bg-zinc-900/40'
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <span className="text-sm">🇪🇸</span>
                      <span>Español</span>
                    </span>
                    {language === 'es' && <Check className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </>
            )}
          </div>

          {user ? (
            <>
              {/* Daily Quota Chip (Desktop) */}
              {quota && (
                <div className="hidden lg:flex items-center gap-2 px-2.5 py-1 rounded-lg bg-zinc-900/90 border border-zinc-800 text-zinc-300">
                  <span className="text-zinc-500 text-[11px]">{t.nav.today}</span>
                  <span className="font-medium text-white">
                    {quota.videosRemainingToday} {t.nav.of} {quota.maxDailyVideos ?? quota.maxDailyJobs ?? 1}
                  </span>
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase ${
                    user.plan === 'premium' ? 'bg-zinc-800 text-zinc-200' : 'bg-zinc-900 text-zinc-400'
                  }`}>
                    {user.plan}
                  </span>
                </div>
              )}

              {user.plan === 'free' && (
                <button
                  onClick={onUpgradeClick}
                  className="btn-primary text-xs py-1.5 px-2.5 sm:px-3 font-medium hidden sm:flex items-center gap-1.5"
                >
                  <Sparkles className="w-3 h-3 text-zinc-950" />
                  <span>{t.nav.upgrade}</span>
                </button>
              )}

              {/* User Avatar & Settings (Desktop) */}
              <div className="hidden sm:flex items-center gap-1 pl-1.5 border-l border-zinc-800">
                {(user.picture || user.avatar) ? (
                  <img
                    src={user.picture || user.avatar || ''}
                    alt={user.name || 'Usuario'}
                    referrerPolicy="no-referrer"
                    className="w-6 h-6 rounded-full border border-zinc-700 object-cover"
                  />
                ) : (
                  <div className="w-6 h-6 rounded-full bg-zinc-800 flex items-center justify-center text-zinc-400">
                    <UserIcon className="w-3 h-3" />
                  </div>
                )}

                {onSettingsClick && (
                  <button
                    onClick={onSettingsClick}
                    title={t.nav.settings}
                    className="p-1.5 text-zinc-400 hover:text-white rounded-md hover:bg-zinc-900 transition-colors"
                  >
                    <Settings className="w-3.5 h-3.5" />
                  </button>
                )}

                <button
                  onClick={onLogoutClick}
                  title={t.nav.signOut}
                  className="p-1.5 text-zinc-400 hover:text-zinc-200 rounded-md hover:bg-zinc-900 transition-colors"
                >
                  <LogOut className="w-3.5 h-3.5" />
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Google Login Button */}
              <button
                onClick={onLoginClick}
                className="btn-primary text-xs py-1.5 px-3 font-medium whitespace-nowrap"
              >
                {t.nav.signIn}
              </button>
            </>
          )}

          {/* Mobile Hamburger Toggle Button (Visible on < md) */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 text-zinc-400 hover:text-white rounded-lg hover:bg-zinc-900 border border-zinc-800 transition-colors focus:outline-none"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </button>
        </div>

      </div>

      {/* Mobile Drawer Navigation */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-zinc-800/80 bg-[#09090b]/98 px-4 py-4 space-y-4 animate-in slide-in-from-top-2 duration-200">
          
          {/* User info if logged in (Mobile) */}
          {user && (
            <div className="p-3 rounded-xl bg-zinc-900/50 border border-zinc-800/80 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {(user.picture || user.avatar) ? (
                    <img
                      src={user.picture || user.avatar || ''}
                      alt={user.name || 'Usuario'}
                      referrerPolicy="no-referrer"
                      className="w-7 h-7 rounded-full border border-zinc-700 object-cover"
                    />
                  ) : (
                    <div className="w-7 h-7 rounded-full bg-zinc-800 flex items-center justify-center text-zinc-400">
                      <UserIcon className="w-3.5 h-3.5" />
                    </div>
                  )}
                  <div>
                    <p className="text-xs font-medium text-white">{user.name || 'Usuario'}</p>
                    <p className="text-[10px] text-zinc-500 truncate max-w-[160px]">{user.email}</p>
                  </div>
                </div>
                <span className="text-[10px] uppercase font-semibold px-2 py-0.5 rounded bg-zinc-800 text-zinc-300">
                  {user.plan}
                </span>
              </div>

              {quota && (
                <div className="pt-2 border-t border-zinc-800/60 flex items-center justify-between text-[11px] text-zinc-400">
                  <span>{t.nav.today}</span>
                  <span className="text-white font-medium">
                    {quota.videosRemainingToday} {t.nav.of} {quota.maxDailyVideos ?? quota.maxDailyJobs ?? 1}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Mobile Language Switcher */}
          <div className="p-2.5 rounded-xl bg-zinc-900/40 border border-zinc-800 flex items-center justify-between text-xs">
            <span className="text-zinc-400 flex items-center gap-2">
              <Globe className="w-3.5 h-3.5" />
              <span>Language / Idioma</span>
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setLanguage('en')}
                className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                  language === 'en'
                    ? 'bg-zinc-100 text-zinc-950 font-semibold'
                    : 'text-zinc-400 hover:text-white'
                }`}
              >
                🇺🇸 EN
              </button>
              <button
                onClick={() => setLanguage('es')}
                className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                  language === 'es'
                    ? 'bg-zinc-100 text-zinc-950 font-semibold'
                    : 'text-zinc-400 hover:text-white'
                }`}
              >
                🇪🇸 ES
              </button>
            </div>
          </div>

          {/* Navigation Links (Mobile) */}
          <nav className="space-y-1 text-sm">
            <button
              onClick={() => handleNav('home')}
              className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-left transition-colors w-full ${
                currentView === 'home'
                  ? 'bg-zinc-800 text-white font-medium'
                  : 'text-zinc-300 hover:bg-zinc-900/60'
              }`}
            >
              <Home className="w-4 h-4 text-zinc-400" />
              <span>{t.nav.home}</span>
            </button>

            {user && (
              <button
                onClick={() => handleNav('dashboard')}
                className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-left transition-colors w-full ${
                  currentView === 'dashboard' || currentView === 'workflow'
                    ? 'bg-zinc-800 text-white font-medium'
                    : 'text-zinc-300 hover:bg-zinc-900/60'
                }`}
              >
                <LayoutDashboard className="w-4 h-4 text-zinc-400" />
                <span>{t.nav.dashboard}</span>
              </button>
            )}

            <button
              onClick={() => handleNav('pricing')}
              className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-left transition-colors w-full ${
                currentView === 'pricing'
                  ? 'bg-zinc-800 text-white font-medium'
                  : 'text-zinc-300 hover:bg-zinc-900/60'
              }`}
            >
              <CreditCard className="w-4 h-4 text-zinc-400" />
              <span>{t.nav.pricing}</span>
            </button>

            <button
              onClick={() => handleNav('guides')}
              className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-left transition-colors w-full ${
                currentView === 'guides'
                  ? 'bg-zinc-800 text-white font-medium'
                  : 'text-zinc-300 hover:bg-zinc-900/60'
              }`}
            >
              <HelpCircle className="w-4 h-4 text-zinc-400" />
              <span>{t.nav.guides}</span>
            </button>

            <button
              onClick={() => handleNav('legal', 'faq')}
              className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-left transition-colors w-full ${
                currentView === 'legal' && legalTab === 'faq'
                  ? 'bg-zinc-800 text-white font-medium'
                  : 'text-zinc-300 hover:bg-zinc-900/60'
              }`}
            >
              <HelpCircle className="w-4 h-4 text-zinc-400" />
              <span>{t.nav.howItWorks}</span>
            </button>

            <button
              onClick={() => handleNav('legal', 'privacy')}
              className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-left transition-colors w-full ${
                currentView === 'legal' && (legalTab === 'privacy' || legalTab === 'cookies')
                  ? 'bg-zinc-800 text-white font-medium'
                  : 'text-zinc-300 hover:bg-zinc-900/60'
              }`}
            >
              <Shield className="w-4 h-4 text-zinc-400" />
              <span>{t.nav.privacy}</span>
            </button>

            <button
              onClick={() => handleNav('legal', 'terms')}
              className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-left transition-colors w-full ${
                currentView === 'legal' && legalTab === 'terms'
                  ? 'bg-zinc-800 text-white font-medium'
                  : 'text-zinc-300 hover:bg-zinc-900/60'
              }`}
            >
              <FileText className="w-4 h-4 text-zinc-400" />
              <span>{t.nav.terms}</span>
            </button>
          </nav>

          {/* Footer actions for mobile drawer */}
          {user && (
            <div className="pt-3 border-t border-zinc-800/80 flex items-center justify-between text-xs text-zinc-400">
              {onSettingsClick && (
                <button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    onSettingsClick();
                  }}
                  className="flex items-center gap-1.5 py-1.5 px-2 hover:text-white rounded-md hover:bg-zinc-900"
                >
                  <Settings className="w-3.5 h-3.5" />
                  <span>{t.nav.settings}</span>
                </button>
              )}

              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  onLogoutClick();
                }}
                className="flex items-center gap-1.5 py-1.5 px-2 text-red-400 hover:text-red-300 rounded-md hover:bg-zinc-900"
              >
                <LogOut className="w-3.5 h-3.5" />
                <span>{t.nav.signOut}</span>
              </button>
            </div>
          )}

        </div>
      )}
    </header>
  );
};
